import React, { useState, useEffect } from 'react';
import { getVirtualCardProducts } from '../../../../api/api';
import CardsProgressSteps from '../../../../components/ProgressStep/CardsProgressSteps';

import chooseCardIcon1 from '../../../../assets/img/icon/card_verve-card.png';
import chooseCardIcon2 from '../../../../assets/img/icon/card_credit-card.png';
import chooseCardIcon3 from '../../../../assets/img/icon/card_bitcoin-credit-card.png';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaAngleLeft } from 'react-icons/fa6';

const CreateCard1 = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Dropdown options
  const [brands, setBrands] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [types, setTypes] = useState([]);

  // Selected values
  const [selectedBrand, setSelectedBrand] = useState('');
  const [selectedCurrency, setSelectedCurrency] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [accountPurpose, setAccountPurpose] = useState(location.state?.accountPurpose || '')
  const [expectedMonthlyVolume, setExpectedMonthlyVolume] = useState(location.state?.expectedMonthlyVolume || '')

  const [selectedCard, setSelectedCard] = useState(location.state?.selectedCardData?.id || null);
  const [dynamicOptions, setDynamicOptions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await getVirtualCardProducts();
        const data = response.data?.data;
        
        if (data) {
          setBrands(data.brands || []);
          setCurrencies(data.currencies || []);
          setTypes(data.type || ['virtual']);

          const options = (data.virtual_card_products || []).map((product) => {
            const brand = product.brand;
            const currency = product.currency;
            const type = product.type || 'virtual';
            return {
              id: `${brand}-${currency}`,
              slug: brand,
              brand: brand,
              currency: currency,
              type: type,
              name: `${currency} ${brand.toUpperCase()} Card`,
              icon: brand.toLowerCase() === 'visa' ? chooseCardIcon3 : brand.toLowerCase() === 'mastercard' ? chooseCardIcon2 : chooseCardIcon1,
              fee_configurations: product.fee_configurations || [],
              supported_countries: data.supported_countries || []
            };
          });
          setDynamicOptions(options);

          // If a card was previously selected, pre-fill dropdowns
          const preselectedCardId = location.state?.selectedCardData?.id;
          if (preselectedCardId) {
            const match = options.find(opt => opt.id === preselectedCardId);
            if (match) {
              setSelectedBrand(match.brand);
              setSelectedCurrency(match.currency);
              setSelectedType(match.type);
            }
          }
        }
      } catch (error) {
        // console.error("Failed to fetch card products", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchProducts();
  }, []);

  useEffect(() => {
    if (selectedBrand && selectedCurrency && selectedType) {
      const match = dynamicOptions.find(
        opt => opt.brand === selectedBrand && opt.currency === selectedCurrency && opt.type === selectedType
      );
      if (match) {
        setSelectedCard(match.id);
      } else {
        setSelectedCard(null);
      }
    } else {
      setSelectedCard(null);
    }
  }, [selectedBrand, selectedCurrency, selectedType, dynamicOptions]);

  const handleContinue = () => {
    if (selectedCard) {
      const selectedCardData = dynamicOptions.find(card => card.id === selectedCard);
      navigate("/user/cards/step2", { state: { selectedCardData, accountPurpose, expectedMonthlyVolume} });
    }
  }

  const handleGoBack = () => {
    navigate("/user/cards"); 
  };

  return (
    <section className='create__card__page pt-4'>
      <div className="d-flex align-items-center mb-4 px-md-3">
        <button 
          onClick={handleGoBack} 
          className="btn btn-light d-flex align-items-center shadow-sm" 
          style={{ 
            borderRadius: '20px', 
            padding: '6px 16px',
            backgroundColor: '#ffffff',
            border: '1px solid #E0E4EC',
            color: '#19064B',
            fontWeight: '500',
            fontSize: '13px',
            gap: '6px'
          }}
        >
          <FaAngleLeft size={13} />
          <span>Go back</span>
        </button>
      </div>

      <section><CardsProgressSteps currentStep={1} /></section>

      <section className="choose__card-section">
        <div className="container">
          <div className='create_card_header text-center mt-4 mb-4'>
            <h4 className='mb-2 fw-semibold' style={{ color: '#19064B' }}>Choose Card Type</h4>
            <p className='text-muted small'>Select a card network to proceed with creating your virtual card.</p>
          </div>

          <div className="create-card_section card-selection mb-5">
            {/* Card Selection */}
            {loading ? (
              <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>
            ) : (
              <div className="card-selection-form" style={{ maxWidth: '400px', margin: '0 auto', textAlign: 'left' }}>
                <div className="mb-4">
                  <label className="form-label fw-medium text-muted mb-2" style={{ fontSize: '13px' }}>
                    Card Brand
                  </label>
                  <select 
                    className="form-select form-select-lg shadow-sm"
                    value={selectedBrand}
                    onChange={(e) => setSelectedBrand(e.target.value)}
                    style={{ fontSize: '15px', borderRadius: '12px', border: '1px solid #E0E4EC', color: '#19064B' }}
                  >
                    <option value="">Select Brand</option>
                    {brands.map((b) => (
                      <option key={b} value={b}>{b.toUpperCase()}</option>
                    ))}
                  </select>
                </div>

                <div className="mb-4">
                  <label className="form-label fw-medium text-muted mb-2" style={{ fontSize: '13px' }}>
                    Currency
                  </label>
                  <select 
                    className="form-select form-select-lg shadow-sm"
                    value={selectedCurrency}
                    onChange={(e) => setSelectedCurrency(e.target.value)}
                    style={{ fontSize: '15px', borderRadius: '12px', border: '1px solid #E0E4EC', color: '#19064B' }}
                  >
                    <option value="">Select Currency</option>
                    {currencies.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>

                <div className="mb-4">
                  <label className="form-label fw-medium text-muted mb-2" style={{ fontSize: '13px' }}>
                    Card Type
                  </label>
                  <select 
                    className="form-select form-select-lg shadow-sm"
                    value={selectedType}
                    onChange={(e) => setSelectedType(e.target.value)}
                    style={{ fontSize: '15px', borderRadius: '12px', border: '1px solid #E0E4EC', color: '#19064B' }}
                  >
                    <option value="">Select Type</option>
                    {types.map((t) => (
                      <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                    ))}
                  </select>
                </div>

                {selectedBrand && selectedCurrency && selectedType && !selectedCard && (
                  <div className="alert alert-warning mt-3 border-0 shadow-sm" style={{ fontSize: '13.5px', borderRadius: '10px', backgroundColor: '#FFF5F5', color: '#C53030' }}>
                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                    This card configuration is currently not available.
                  </div>
                )}

                <div className="mb-4">
                  <label className="form-label fw-medium text-muted mb-2" style={{ fontSize: '13px' }}>
                      What will you use this card for? <span className="text-danger">*</span>
                  </label>
                  <select 
                    className="form-select form-select-lg shadow-sm" 
                    value={accountPurpose} 
                    onChange={(e) => setAccountPurpose(e.target.value)} 
                    style={{ fontSize: '15px', borderRadius: '12px', border: '1px solid #E0E4EC', color: '#19064B' }}
                    required
                  >
                    <option value="" disabled>Select card purpose</option>
                    <option value="online_shopping">Online Shopping</option>
                    <option value="subscriptions">Subscriptions & Digital Services</option>
                    <option value="advertising">Digital Advertising</option>
                    <option value="business_expenses">Business Expenses</option>
                    <option value="travel">Travel & Accommodation</option>
                    <option value="education">Education & Online Courses</option>
                    <option value="software_tools">Software & Work Tools</option>
                    <option value="freelancing">Freelancing & Remote Work</option>
                    <option value="personal_expenses">Personal Expenses</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="row g-3 mb-4">
                  <label className="form-label fw-medium text-muted mb-2" style={{ fontSize: '13px' }}>
                      Expected Monthly Spend <span className="text-danger">*</span>
                  </label>
                  <div className="input-group mt-0">
                      <span className="input-group-text">{selectedCurrency}</span>
                      <input type="number" className="form-control"
                        value={expectedMonthlyVolume}
                        onChange={(e) => setExpectedMonthlyVolume(e.target.value)}
                        placeholder="0.00" min="0" required
                      />
                  </div>
                </div>
              </div>
            )}

            {/* Continue Button */}
            <div className="d-flex align-items-center justify-content-center mt-5">
              <button onClick={handleContinue} className="btn primary__btn w-100 py-3 fw-medium" disabled={!selectedCard || loading} style={{ maxWidth: '300px', fontSize: '15px' }}>
                Continue
              </button>
            </div>
          </div>
        </div>
      </section>
    </section>
  )
}

export default CreateCard1;